import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from 'assets/search-icon.svg';
import CustomTextField from 'components/shared/CustomTextField/CustomTextField';
import { FormikDatePicker } from 'components/shared/Form/DatePicker';
import SelectField from 'components/shared/Form/SelectField';
import { useFormikContext } from 'formik';
import { FieldConfig } from 'model/deals.model';
import { NumericFormat } from 'react-number-format';
import zeplinColor from 'theme/Color';

interface GenericSectionProps {
  fields: FieldConfig[];
  dropdowns?: Record<string, string[] | { [key: string]: string }[]>;
  autocompleteMap?: {
    client: any[];
    business: any[];
    owner: any[];
  };
}

const getValue = (data: any[], value: string) => data.find((obj) => obj.id === value) || null;

const GenericSection = ({ fields, dropdowns = {}, autocompleteMap }: GenericSectionProps) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xl'));
  const { values, handleChange, handleBlur, setFieldValue } = useFormikContext<any>();
  const isClientLocked = Boolean(values?.lockClient);

  const renderField = (f: FieldConfig) => {
    const value = values[f.name] ?? '';

    switch (f.type) {
      case 'date':
        return <FormikDatePicker name={f.name} label={f.label} required={f.required ?? false} />;

      case 'checkbox':
        return (
          <FormControlLabel
            label={f.label}
            control={
              <Checkbox
                name={f.name}
                checked={Boolean(value)}
                onChange={(e) =>
                  handleChange({ target: { name: f.name, value: e.target.checked } } as any)
                }
                onBlur={handleBlur}
              />
            }
          />
        );

      case 'select':
        let options = dropdowns[f.name] || f.options || [];

        return (
          <SelectField
            field={f}
            value={value}
            options={options}
            onChange={handleChange}
            onBlur={handleBlur}
            isXs={isXs}
          />
        );

      case 'autocomplete':
        if (!autocompleteMap) return;
        const data = autocompleteMap[f.name as keyof typeof autocompleteMap];
        let valueId = value.cognitoSub || value.accountId || value.businessId || value.id;

        let selectedValue = getValue(data, valueId) || null;

        return (
          <Autocomplete
            size={isXs ? 'small' : 'medium'}
            value={selectedValue}
            disabled={f.name === 'client' && isClientLocked}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{
                  fontSize: isXs ? 14 : 16,
                  '& .MuiFormLabel-asterisk': {
                    color: zeplinColor.Error,
                  },
                }}
                required={f.required ?? false}
                label={<Typography variant='responsiveBody2'>{f.label}</Typography>}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <InputAdornment position='end'>
                        <img src={SearchIcon} width={20} height={20} alt='search' />
                      </InputAdornment>
                    ),
                    style: { paddingRight: 14 },
                  },
                }}
              />
            )}
            onChange={(_, newValue) => {
              const value = getValue(data, newValue?.id);

              handleChange({
                target: {
                  name: f.name,
                  value: value,
                },
              });
            }}
            onBlur={handleBlur}
            options={data}
            getOptionKey={(option) => option.id}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option) => option.id === value}
          />
        );

      case 'percentage':
        return (
          <NumericFormat
            isAllowed={(values) => Number(values.value) <= 100}
            customInput={TextField}
            required={f.required}
            onBlur={handleBlur}
            sx={{
              width: '100%',
              flex: 1,
              '& input[type=number]': {
                MozAppearance: 'textfield',
              },
            }}
            slotProps={{
              input: { sx: { fontSize: isXs ? 14 : 16 } },
              inputLabel: { size: isXs ? 'small' : 'medium', sx: { fontSize: isXs ? 14 : 16 } },
            }}
            size={isXs ? 'small' : 'medium'}
            value={value}
            label={f.label}
            name={f.name}
            onValueChange={(values) => {
              setFieldValue(f.name, values.floatValue);
            }}
            suffix='%'
            thousandSeparator
          />
        );

      case 'amount':
        return (
          <NumericFormat
            customInput={TextField}
            required={f.required}
            onBlur={handleBlur}
            sx={{
              width: '100%',
              flex: 1,
              '& input[type=number]': {
                MozAppearance: 'textfield',
              },
            }}
            slotProps={{
              input: { sx: { fontSize: isXs ? 14 : 16 } },
              inputLabel: { size: isXs ? 'small' : 'medium', sx: { fontSize: isXs ? 14 : 16 } },
            }}
            size={isXs ? 'small' : 'medium'}
            value={value}
            label={f.label}
            name={f.name}
            onValueChange={(values) => {
              setFieldValue(f.name, values.floatValue);
            }}
            prefix='$'
            thousandSeparator
          />
        );

      default:
        return (
          <CustomTextField
            required={f.required}
            name={f.name}
            label={f.label}
            type={'text'}
            text={value}
            onChange={handleChange}
            onBlur={() => handleBlur(f.name)}
            multiline={f.multiline}
            form={true}
            style={{ width: '100%' }}
          />
        );
    }
  };

  return (
    <Box
      sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '24px', width: '100%' }}
    >
      {fields.map((f, i) => (
        <Box key={`${f.name}-${i}`} sx={{ gridColumn: { xs: 'span 4' } }}>
          {renderField(f)}
        </Box>
      ))}
    </Box>
  );
};

export default GenericSection;
